import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, Plus, X, Search, Trash2, ListChecks } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (n) => {
  const v = Number(n) || 0;
  return v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const iso = (d) => d.toISOString().slice(0, 10);

// Shared with MonthlyDataSheet: the queue drives the sheet's item columns.
export const ITEM_TRACKER_QUEUE_KEY = "item_tracker_queue";
export const ITEM_TRACKER_QUEUE_EVENT = "item-tracker-queue-changed";

// Item Tracker Report — pick a queue of item names (e.g. "Cigarette Packs",
// "State Tax (6%)", "Network Revenue") and get a per-item breakdown across
// a date range, plus a consolidated group total. Complements the departmental
// report (which always includes every debit row).
export default function ItemTrackerReport() {
  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const [startDate, setStartDate] = useState(iso(firstOfMonth));
  const [endDate, setEndDate] = useState(iso(today));
  const [granularity, setGranularity] = useState("day");

  const [availableItems, setAvailableItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [pickerQuery, setPickerQuery] = useState("");
  const [selectedItems, setSelectedItems] = useState(() => {
    try {
      const q = JSON.parse(localStorage.getItem(ITEM_TRACKER_QUEUE_KEY) || "[]");
      return Array.isArray(q) ? q.filter((x) => typeof x === "string" && x.trim()) : [];
    } catch { return []; }
  }); // ordered queue of item names — persisted + shared with Monthly Data Sheet
  useEffect(() => {
    try { localStorage.setItem(ITEM_TRACKER_QUEUE_KEY, JSON.stringify(selectedItems)); } catch { /* ignore */ }
    window.dispatchEvent(new Event(ITEM_TRACKER_QUEUE_EVENT));
  }, [selectedItems]);
  const [report, setReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);

  // Load available items whenever date range changes so the picker reflects
  // what actually exists in the currently-selected window.
  const fetchAvailable = useCallback(async () => {
    setLoadingItems(true);
    try {
      const res = await axios.get(`${API}/reports/store-paperwork/available-items`, {
        params: { start_date: startDate, end_date: endDate },
      });
      setAvailableItems(res.data?.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load item list");
    } finally {
      setLoadingItems(false);
    }
  }, [startDate, endDate]);

  useEffect(() => { fetchAvailable(); }, [fetchAvailable]);

  const filteredPicker = useMemo(() => {
    const q = pickerQuery.trim().toLowerCase();
    // Hide items already in the queue
    const chosen = new Set(selectedItems.map((s) => s.toLowerCase()));
    return availableItems.filter((it) => {
      if (chosen.has(it.name.toLowerCase())) return false;
      if (!q) return true;
      return it.name.toLowerCase().includes(q);
    }).slice(0, 30);
  }, [availableItems, pickerQuery, selectedItems]);

  const addItem = (name) => {
    const trimmed = (name || "").trim();
    if (!trimmed) return;
    if (selectedItems.some((s) => s.toLowerCase() === trimmed.toLowerCase())) return;
    setSelectedItems([...selectedItems, trimmed]);
    setPickerQuery("");
  };

  const removeItem = (name) => {
    setSelectedItems(selectedItems.filter((s) => s.toLowerCase() !== name.toLowerCase()));
  };

  const clearQueue = () => {
    setSelectedItems([]);
    setReport(null);
  };

  const generateReport = useCallback(async () => {
    if (selectedItems.length === 0) {
      toast.error("Add at least one item to the queue first");
      return;
    }
    setLoadingReport(true);
    try {
      // Send each item as its own repeated query param
      const params = new URLSearchParams();
      params.append("start_date", startDate);
      params.append("end_date", endDate);
      params.append("granularity", granularity);
      selectedItems.forEach((name) => params.append("item", name));
      const res = await axios.get(`${API}/reports/store-paperwork/items?${params.toString()}`);
      setReport(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate report");
      setReport(null);
    } finally {
      setLoadingReport(false);
    }
  }, [startDate, endDate, granularity, selectedItems]);

  const applyPreset = (preset) => {
    const now = new Date();
    if (preset === "today") {
      const t = iso(now);
      setStartDate(t); setEndDate(t); setGranularity("day");
    } else if (preset === "week") {
      const s = new Date(now); s.setDate(now.getDate() - 6);
      setStartDate(iso(s)); setEndDate(iso(now)); setGranularity("day");
    } else if (preset === "month") {
      setStartDate(iso(new Date(now.getFullYear(), now.getMonth(), 1)));
      setEndDate(iso(now));
      setGranularity("day");
    } else if (preset === "year") {
      setStartDate(iso(new Date(now.getFullYear(), 0, 1)));
      setEndDate(iso(now));
      setGranularity("month");
    }
  };

  const cols = report?.columns || [];
  const rows = report?.rows || [];
  const colTotals = report?.column_totals || [];
  const grand = report?.grand_total || 0;

  return (
    <div className="space-y-4" data-testid="item-tracker-report">
      {/* Filter + item picker toolbar */}
      <Card className="border-primary/40 bg-primary/5">
        <CardContent className="p-4 space-y-4">
          {/* Date range + granularity + presets */}
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-[10px] font-mono uppercase text-muted-foreground mb-1">Start date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="h-8 px-2 rounded-md border border-border bg-background font-mono text-xs"
                data-testid="item-report-start-date"
              />
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase text-muted-foreground mb-1">End date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="h-8 px-2 rounded-md border border-border bg-background font-mono text-xs"
                data-testid="item-report-end-date"
              />
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase text-muted-foreground mb-1">Group by</label>
              <select
                value={granularity}
                onChange={(e) => setGranularity(e.target.value)}
                className="h-8 px-2 rounded-md border border-border bg-background font-mono text-xs"
                data-testid="item-report-granularity"
              >
                <option value="day">Day</option>
                <option value="month">Month</option>
                <option value="year">Year</option>
              </select>
            </div>
            <div className="flex flex-wrap gap-1">
              <Button size="sm" variant="outline" className="btn-3d h-8" onClick={() => applyPreset("today")} data-testid="item-report-preset-today">Today</Button>
              <Button size="sm" variant="outline" className="btn-3d h-8" onClick={() => applyPreset("week")} data-testid="item-report-preset-week">Week</Button>
              <Button size="sm" variant="outline" className="btn-3d h-8" onClick={() => applyPreset("month")} data-testid="item-report-preset-month">Month</Button>
              <Button size="sm" variant="outline" className="btn-3d h-8" onClick={() => applyPreset("year")} data-testid="item-report-preset-year">Year</Button>
            </div>
          </div>

          {/* Item picker + selected queue */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* LEFT: search & available items */}
            <div>
              <label className="block text-[10px] font-mono uppercase text-muted-foreground mb-1 flex items-center gap-1">
                <Search className="w-3 h-3" /> Search &amp; add items
              </label>
              <Input
                value={pickerQuery}
                onChange={(e) => setPickerQuery(e.target.value)}
                placeholder="Type to filter (e.g. cigarette, tax, network)…"
                className="h-8 font-mono text-xs"
                data-testid="item-report-search"
              />
              <div className="mt-2 border border-border/40 rounded-md max-h-48 overflow-y-auto bg-background/60">
                {loadingItems ? (
                  <p className="text-center font-mono text-[10px] text-muted-foreground py-3">Loading items…</p>
                ) : filteredPicker.length === 0 ? (
                  <p className="text-center font-mono text-[10px] text-muted-foreground py-3">
                    {availableItems.length === 0
                      ? "No items yet — save some Store Paperwork data first."
                      : "No matches. Try a different search."}
                  </p>
                ) : (
                  filteredPicker.map((it) => (
                    <button
                      key={`${it.side}::${it.name}`}
                      onClick={() => addItem(it.name)}
                      className="w-full flex items-center justify-between gap-2 px-2 py-1.5 hover:bg-primary/10 text-left font-mono text-xs transition-colors"
                      data-testid={`item-report-add-${it.name.replace(/\s/g, "_")}`}
                    >
                      <div className="flex items-center gap-1.5 min-w-0">
                        <Plus className="w-3 h-3 text-primary flex-shrink-0" />
                        <span className="truncate">{it.name}</span>
                        {it.auto && (
                          <span className="text-[8px] uppercase font-bold px-1 py-0.5 rounded bg-emerald-500/20 text-emerald-500">auto</span>
                        )}
                      </div>
                      <span className={`text-[9px] uppercase font-mono px-1 rounded ${it.side === "debit" ? "bg-blue-500/20 text-blue-500" : it.side === "credit" ? "bg-purple-500/20 text-purple-500" : "bg-amber-500/20 text-amber-500"}`}>
                        {it.side}
                      </span>
                    </button>
                  ))
                )}
              </div>
            </div>

            {/* RIGHT: selected queue */}
            <div>
              <label className="block text-[10px] font-mono uppercase text-muted-foreground mb-1 flex items-center gap-1">
                <ListChecks className="w-3 h-3" /> Report queue ({selectedItems.length})
              </label>
              <div className="border border-border/40 rounded-md min-h-[196px] max-h-48 overflow-y-auto bg-background/60 p-2">
                {selectedItems.length === 0 ? (
                  <p className="text-center font-mono text-[10px] text-muted-foreground py-16">
                    No items queued. Click ⨁ next to items on the left to add them here, then hit&nbsp;<b>Generate report</b>.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {selectedItems.map((name, i) => (
                      <Badge
                        key={name}
                        variant="secondary"
                        className="flex items-center gap-1 pl-2 pr-1 py-1 font-mono text-xs"
                        data-testid={`item-report-queued-${i}`}
                      >
                        <span className="max-w-[200px] truncate">{name}</span>
                        <button
                          onClick={() => removeItem(name)}
                          className="ml-1 rounded hover:bg-destructive/20 text-destructive p-0.5"
                          title={`Remove ${name}`}
                          data-testid={`item-report-remove-${i}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
              <div className="mt-2 flex items-center justify-end gap-2">
                {selectedItems.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearQueue}
                    className="h-8 text-xs text-muted-foreground"
                    data-testid="item-report-clear-queue"
                  >
                    <Trash2 className="w-3 h-3 mr-1" /> Clear
                  </Button>
                )}
                <Button
                  size="sm"
                  onClick={generateReport}
                  disabled={loadingReport || selectedItems.length === 0}
                  className="btn-3d h-8"
                  data-testid="item-report-generate-btn"
                >
                  <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loadingReport ? "animate-spin" : ""}`} />
                  {loadingReport ? "Generating…" : `Generate report (${selectedItems.length})`}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Report table */}
      {report && (
        <Card className="border-border/50" data-testid="item-report-results">
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="font-mono text-sm">
              Item Report · {report.start_date} → {report.end_date}
            </CardTitle>
            <span className="font-mono text-xs text-muted-foreground">
              {rows.length} item{rows.length === 1 ? "" : "s"} · {cols.length} column{cols.length === 1 ? "" : "s"} · {report.docs_scanned || 0} date{(report.docs_scanned || 0) === 1 ? "" : "s"}
            </span>
          </CardHeader>
          <CardContent>
            {rows.length === 0 ? (
              <p className="text-center font-mono text-xs text-muted-foreground py-8">
                No data for the selected items in this range.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-xs font-mono tabular-nums" data-testid="item-report-table">
                  <thead>
                    <tr className="border-b border-border/50">
                      <th className="text-left px-2 py-2 font-semibold sticky left-0 bg-card">Item</th>
                      <th className="text-left px-2 py-2 font-semibold">Side</th>
                      {cols.map((c) => (
                        <th key={c} className="text-right px-2 py-2 font-semibold whitespace-nowrap">{c}</th>
                      ))}
                      <th className="text-right px-2 py-2 font-semibold bg-emerald-500/10">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={r.item} className={`border-b border-border/20 hover:bg-muted/30 ${r.no_data ? "opacity-50" : ""}`} data-testid={`item-report-row-${i}`}>
                        <td className="px-2 py-1.5 sticky left-0 bg-card font-semibold">
                          {r.item}
                          {r.no_data && <span className="ml-2 text-[9px] uppercase text-orange-400">no data</span>}
                        </td>
                        <td className="px-2 py-1.5">
                          <span className={`text-[9px] uppercase font-mono px-1.5 py-0.5 rounded ${r.side === "debit" ? "bg-blue-500/20 text-blue-500" : r.side === "credit" ? "bg-purple-500/20 text-purple-500" : r.side === "computed" ? "bg-amber-500/20 text-amber-500" : "bg-muted text-muted-foreground"}`}>
                            {r.side}
                          </span>
                        </td>
                        {r.amounts.map((a, j) => (
                          <td key={j} className={`text-right px-2 py-1.5 ${a > 0 ? "" : "text-muted-foreground"}`}>{a ? fmtMoney(a) : "—"}</td>
                        ))}
                        <td className="text-right px-2 py-1.5 font-bold bg-emerald-500/10 text-emerald-400">{fmtMoney(r.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-border/70 bg-muted/40" data-testid="item-report-totals-row">
                      <td colSpan={2} className="px-2 py-2 font-bold sticky left-0 bg-muted/40">Group Total</td>
                      {colTotals.map((t, i) => (
                        <td key={i} className="text-right px-2 py-2 font-bold">{fmtMoney(t)}</td>
                      ))}
                      <td className="text-right px-2 py-2 font-bold text-emerald-400 text-base bg-emerald-500/20" data-testid="item-report-grand-total">{fmtMoney(grand)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
